import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../hooks/useAuth";
import { PaymentsTable } from "../components/PaymentsTable";
import { PaymentsFilters } from "../components/PaymentsFilters";
import { usePayments, PaymentFilters } from "../api/users";
import AddPaymentModal from "../components/modals/AddPaymentModal";
import { useQueryClient } from "@tanstack/react-query";

const PaymentsPage: React.FC = () => {
    const { t } = useTranslation("payments");
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [filters, setFilters] = useState<PaymentFilters>({
        per_page: 20,
        page: 1,
    });

    const { data, isLoading, error } = usePayments(filters);

    // Проверяем, является ли пользователь администратором
    const isAdmin = user?.roles?.includes("admin") || false;

    const handlePageChange = (page: number) => {
        setFilters((prev) => ({ ...prev, page }));
    };

    const handlePageSizeChange = (per_page: number) => {
        setFilters((prev) => ({ ...prev, per_page, page: 1 })); // Сбрасываем на первую страницу
    };

    const handlePaymentCreated = () => {
        queryClient.invalidateQueries({ queryKey: ["payments"] });
    };

    // Если пользователь не администратор, показываем сообщение об ошибке
    if (!isAdmin) {
        return (
            <div className="space-y-6">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {t("title")}
                    </h2>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        {t("description")}
                    </p>
                </div>

                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg
                                className="h-5 w-5 text-red-400 dark:text-red-500"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                                />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800 dark:text-red-400">
                                {t("access_denied")}
                            </h3>
                            <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                                {t("access_denied_message")}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-6">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {t("title")}
                    </h2>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        {t("description")}
                    </p>
                </div>

                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg
                                className="h-5 w-5 text-red-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">
                                {t("error.title")}
                            </h3>
                            <div className="mt-2 text-sm text-red-700">
                                {t("error.message")}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="h-screen flex flex-col">
            {/* Фиксированный заголовок */}
            <div className="shadow-sm border-b border-gray-200 p-6 bg-gray-50 dark:bg-gray-800 ">
                <div className="flex justify-between items-center">
                    <div className="flex items-center gap-4">
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                            {t("title")}
                        </h2>
                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            Добавить
                        </button>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                        {data?.meta?.total && (
                            <span>
                                {t("total_payments")}: {data.meta.total}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Фиксированные фильтры */}
            <div className="shadow-sm border-b border-gray-200 dark:border-gray-700 p-6 bg-gray-50 dark:bg-gray-800">
                <PaymentsFilters
                    filters={filters}
                    onFiltersChange={setFilters}
                    uniqueDescs={data?.unique_descs || []}
                />
            </div>

            {/* Прокручиваемая таблица */}
            <div className="flex-1">
                <PaymentsTable
                    data={data?.data || []}
                    isLoading={isLoading}
                    pagination={
                        data?.meta || {
                            current_page: 1,
                            last_page: 1,
                            per_page: 20,
                            total: 0,
                        }
                    }
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
            </div>

            <AddPaymentModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onPaymentCreated={handlePaymentCreated}
            />
        </div>
    );
};

export default PaymentsPage;
